import { faTrash, faEdit, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { deleteData, getData, putData } from '../lib/utils';
import Header from '../components/Headers/Header';

const ClientTable = () => {
  const [tableData, setTableData] = useState([]);
  const [editRowId, setEditRowId] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  const planTypes = ['imageonly', 'photovideo'];
  const billingCycles = ['quarterly', 'halfyearly', 'yearly'];
  const statuses = ['Active', 'Not Active'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getData("admin/all-admin");
        console.log("Fetched data:", response);
        setTableData(response);
      } catch (error) {
        console.error("Error fetching admin data:", error);
      }
    };
    fetchData();
  }, []);

  const handleDelete = async (_id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this row?');
    if (confirmDelete) {
      setTableData(prevData => prevData.filter(item => item._id !== _id));

      const res = await deleteData(`admin/deleteAdmin/${_id}`);
      if (res) {
        console.log("response... ", res)
      }
    }
  };

  const handleEditClick = (row) => {
    setEditRowId(row._id);
    setEditFormData({
      ...row,
      status: row.status || "Active",
      plan: {
        planType: row.plan?.planType || "imageonly",
        photoLimit: row.plan?.photoLimit ?? 20000,
        currentPhotoCount: row.plan?.currentPhotoCount ?? 0,
        totalUploadedPhotos: row.plan?.totalUploadedPhotos ?? 0,
        uploadMultiplier: row.plan?.uploadMultiplier ?? 2,
        billingCycle: row.plan?.billingCycle || "yearly",
      },
      credits: row.credits || 0
    });
  };

  const handleEditChange = (e, field) => {
    let value = e.target.value;
    if (field === 'credits') {
      value = value === '' ? '' : parseFloat(value);
    }
    setEditFormData({ ...editFormData, [field]: value });
  };

  // Nested plan fields need their own handler since plan is now an object
  const handlePlanChange = (e, field) => {
    let value = e.target.value;
    const numericFields = ['photoLimit', 'currentPhotoCount', 'totalUploadedPhotos', 'uploadMultiplier'];
    if (numericFields.includes(field)) {
      value = value === '' ? '' : parseFloat(value);
    }
    setEditFormData(prev => ({
      ...prev,
      plan: { ...prev.plan, [field]: value }
    }));
  };

  const handleUpdate = async (_id) => {
    setTableData(prevData =>
      prevData.map(item => item._id === _id ? { ...editFormData, _id } : item)
    );

    try {
      const obj = editFormData;
      const response = await putData(`admin/admin-update/${_id}`, { obj });
      if (response) {
        console.log("Update Response:", response);
      }
    } catch (error) {
      console.error("Error updating data:", error);
    }

    setEditRowId(null);
    setEditFormData({});
  };

  return (
    <>
      <Header />
      <div className="overflow-x-auto p-4">
        <div className='w-full text-center p-4 font-bold text-lg'>SUPER ADMIN PANEL</div>
        <table className="min-w-full border border-gray-400">
          <thead className="bg-gray-300">
            <tr>
              {[
                'Name', 'Company Name', 'Email', 'Website',
                'Status', 'Plan Type', 'Photo Limit', 'Current Photos',
                'Total Uploaded', 'Multiplier', 'Billing Cycle',
                'Role', 'Actions', 'Login', 'Credits'
              ].map((header, index) => (
                <th
                  key={index}
                  className="text-sm text-center p-2 border border-gray-400 font-semibold"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.map((row) => {
              const isEditing = editRowId === row._id;
              return (
                <tr key={row._id} className="hover:bg-gray-100 text-center">
                  {[
                    'name', 'companyName', 'email', 'websiteUrl',
                  ].map((field, i) => (
                    <td key={i} className="p-2 border border-gray-300">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editFormData[field] || ''}
                          onChange={(e) => handleEditChange(e, field)}
                          className="border w-full p-1"
                        />
                      ) : (
                        row[field]
                      )}
                    </td>
                  ))}

                  {/* Status */}
                  <td className="p-2 border border-gray-300">
                    {isEditing ? (
                      <select
                        value={editFormData.status}
                        onChange={(e) => handleEditChange(e, 'status')}
                        className="border p-1 rounded w-full text-center"
                      >
                        {statuses.map((status, i) => (
                          <option key={i} value={status}>{status}</option>
                        ))}
                      </select>
                    ) : String(row.status)}
                  </td>

                  {/* Plan Type */}
                  <td className="p-2 border border-gray-300">
                    {isEditing ? (
                      <select
                        value={editFormData.plan?.planType}
                        onChange={(e) => handlePlanChange(e, 'planType')}
                        className="border p-1 rounded w-full text-center"
                      >
                        {planTypes.map((type, i) => (
                          <option key={i} value={type}>{type}</option>
                        ))}
                      </select>
                    ) : row.plan?.planType}
                  </td>

                  {/* Photo Limit */}
                  <td className="p-2 border border-gray-300">
                    {isEditing ? (
                      <input
                        type="number"
                        value={editFormData.plan?.photoLimit ?? ''}
                        onChange={(e) => handlePlanChange(e, 'photoLimit')}
                        className="border w-full p-1"
                      />
                    ) : row.plan?.photoLimit}
                  </td>

                  {/* Current Photo Count */}
                  <td className="p-2 border border-gray-300">
                    {isEditing ? (
                      <input
                        type="number"
                        value={editFormData.plan?.currentPhotoCount ?? ''}
                        onChange={(e) => handlePlanChange(e, 'currentPhotoCount')}
                        className="border w-full p-1"
                      />
                    ) : row.plan?.currentPhotoCount}
                  </td>

                  {/* Total Uploaded Photos */}
                  <td className="p-2 border border-gray-300">
                    {isEditing ? (
                      <input
                        type="number"
                        value={editFormData.plan?.totalUploadedPhotos ?? ''}
                        onChange={(e) => handlePlanChange(e, 'totalUploadedPhotos')}
                        className="border w-full p-1"
                      />
                    ) : row.plan?.totalUploadedPhotos}
                  </td>

                  {/* Upload Multiplier */}
                  <td className="p-2 border border-gray-300">
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.plan?.uploadMultiplier ?? ''}
                        onChange={(e) => handlePlanChange(e, 'uploadMultiplier')}
                        className="border w-full p-1"
                      />
                    ) : row.plan?.uploadMultiplier}
                  </td>

                  {/* Billing Cycle */}
                  <td className="p-2 border border-gray-300">
                    {isEditing ? (
                      <select
                        value={editFormData.plan?.billingCycle}
                        onChange={(e) => handlePlanChange(e, 'billingCycle')}
                        className="border p-1 rounded w-full text-center"
                      >
                        {billingCycles.map((cycle, i) => (
                          <option key={i} value={cycle}>{cycle}</option>
                        ))}
                      </select>
                    ) : row.plan?.billingCycle}
                  </td>

                  {/* Role */}
                  <td className="p-2 border border-gray-300">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.role || ''}
                        onChange={(e) => handleEditChange(e, 'role')}
                        className="border w-full p-1"
                      />
                    ) : row.role}
                  </td>

                  {/* Actions */}
                  <td className="p-2 border border-gray-300 flex justify-center space-x-2">
                    {isEditing ? (
                      <button
                        onClick={() => handleUpdate(row._id)}
                        className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleEditClick(row)}
                        className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(row._id)}
                      className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>

                  {/* Login */}
                  <td className="p-2 border border-gray-300">
                    <button
                      onClick={async () => {
                        const response = await getData(`admin/impersonate/${row._id}`);
                        if (response?.url) {
                          window.open(response.url, "_blank");
                        }
                      }}
                      id={row._id}
                      className="bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700"
                    >
                      Login
                    </button>
                  </td>

                  {/* Credits */}
                  <td className="p-2 border border-gray-300">
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.01"
                        value={editFormData.credits ?? ''}
                        onChange={(e) => handleEditChange(e, 'credits')}
                        className="border w-full p-1"
                      />
                    ) : (
                      row.credits !== undefined && row.credits !== null
                        ? (row.credits / 1024 / 5).toFixed(2)
                        : ''
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ClientTable;
